import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/services/api';
import type { Conversation } from '@/types';

type Booking = {
  id: string;
  contactName?: string;
  contactEmail?: string;
  endTime?: string;
  summary?: string;
  status: 'confirmed' | 'cancelled' | 'pending' | 'reschedule_requested';
  source?: string;
  bookingType?: 'event' | 'meeting';
  createdAt?: string;
  updatedAt?: string;
  startTime: string;
};

type DashboardUsage = {
  plan?: string;
  aiChatCredits?: number;
  aiEmailDrafts?: number;
  aiContextTokens?: number;
  tokensUsed?: number;
  aiContext?: {
    requests: number;
    vendorAiContextChars: number;
    estimatedTokens: number;
    knowledgeChars: number;
    styleChars: number;
    brandVoiceChars: number;
    customInstructionChars: number;
    preventedFullContextFallbacks: number;
    preventedFullContextChars: number;
  };
  limits?: {
    aiChatCredits: number | null;
    aiEmailDrafts: number | null;
    aiContextTokens: number | null;
    bookings: number | null;
  };
};

type DashboardStats = {
  messages?: number;
};

type DashboardRoi = {
  recentChats?: number;
  aiGeneratedSummaries?: number;
  clientEmailResponded?: number;
  bookingsMade?: number;
  draftsGenerated?: number;
};

export interface DashboardOverview {
  bookings: Booking[];
  conversations: Conversation[];
  usage?: DashboardUsage;
  stats?: DashboardStats;
  roi?: DashboardRoi;
}

interface DashboardState {
  overview: DashboardOverview | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: DashboardState = {
  overview: null,
  isLoading: false,
  error: null,
  lastFetched: null,
};

// Cache duration: 30 seconds (prevent duplicate fetches)
const CACHE_DURATION = 30000;

export const fetchDashboardOverview = createAsyncThunk(
  'dashboard/fetchOverview',
  async (options: { force?: boolean } | undefined, { getState, rejectWithValue }) => {
    const force = options?.force ?? false;
    const state = getState() as { dashboard: DashboardState };
    const now = Date.now();

    // Return cached data if recent and not forced
    if (
      !force &&
      state.dashboard.overview &&
      state.dashboard.lastFetched &&
      now - state.dashboard.lastFetched < CACHE_DURATION
    ) {
      return state.dashboard.overview;
    }

    try {
      const response = await api.get<DashboardOverview>('/dashboard/overview', {
        cache: !force,
        cacheTTL: 30000,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch dashboard data');
    }
  },
  {
    condition: (options, { getState }) => {
      const force = options?.force ?? false;
      const state = getState() as { dashboard: DashboardState };

      // Skip duplicate non-forced requests while one is already in-flight.
      // This avoids fulfilled actions with placeholder/empty data.
      if (!force && state.dashboard.isLoading) {
        return false;
      }

      return true;
    },
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboard: state => {
      state.overview = null;
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchDashboardOverview.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardOverview.fulfilled, (state, action) => {
        state.isLoading = false;
        state.overview = action.payload;
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(fetchDashboardOverview.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearDashboard } = dashboardSlice.actions;
export default dashboardSlice.reducer;
